import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { IconButton } from "@/sprout/icon_button";
import CloseIcon from "@/sprout/icons/close";
import UploadIcon from "@sprout/icons/upload";
import LoadingSpinner from "@/sprout/loading_spinner";
import { SecondaryButton } from "@sprout/buttons";
import { Body } from "@sprout/typography";
import { Stack } from "@sprout/layout";
import { validateResume } from "@components/quick_apply/util";
import { useShowFlash } from "@sprout/flash";
import useDevice from "@/hooks/use_device";
import type { FileState } from "@/queries/use_profile";
import quickApplyBannerDesktop from "@images/email_campaign/quick_apply_profile_email_desktop_banner.png";
import quickApplyBannerMobile from "@images/email_campaign/quick_apply_profile_email_mobile_banner.png";
import { cn } from "@/lib/utils";
import { useSnowplowTracker, SnowplowEvent } from "@/hooks/use_tracker";
import { useSafePostHog } from "@/hooks/use_posthog";

type Step = "prompt" | "uploading";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DashboardUploadResumeModal = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation("user", { keyPrefix: "dashboard_upload_resume_modal" });
  const { t: tResume } = useTranslation("user");
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();
  const { isMobile } = useDevice();
  const buttonSize = isMobile ? "medium" : "large";

  const { trackEvent } = useSnowplowTracker();
  const posthog = useSafePostHog();

  const [step, setStep] = useState<Step>("prompt");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      posthog?.capture("resume_upload_modal_shown", { source: "dashboard" });
      hasTrackedOpen.current = true;
    }
    if (!open) {
      hasTrackedOpen.current = false;
    }
  }, [open, posthog]);

  useEffect(() => {
    if (open) {
      trackEvent(SnowplowEvent.ResumeModalShown, {
        source: "modal",
        variant: "upload_resume_modal",
      });
    }
  }, [open, trackEvent]);

  const handleClose = useCallback(() => {
    if (step === "uploading" && abortControllerRef.current) {
      abortControllerRef.current.abort();
      trackEvent(SnowplowEvent.ResumeUploadAborted, {
        source: "modal",
        variant: "upload_resume_modal",
      });
    } else if (step === "prompt") {
      trackEvent(SnowplowEvent.ResumeModalDismissed, {
        source: "modal",
        variant: "upload_resume_modal",
      });
    }
    setStep("prompt");
    setDragOver(false);
    setUploadError(null);
    onOpenChange(false);
  }, [step, onOpenChange, trackEvent]);

  const uploadFile = useCallback(
    async (file: File, uploadMethod: "drag_and_drop" | "file_picker") => {
      const resumeUpload: FileState = {
        file,
        filename: file.name,
        url: URL.createObjectURL(file),
        content_type: file.type,
      };

      const resumeError = validateResume(resumeUpload);
      if (resumeError) {
        const errorKey =
          resumeError === "resume.invalidFileType" ? "file_invalid_type" : "file_too_large";
        setUploadError(`${file.name}: ${t(errorKey)}`);
        return;
      }

      setUploadError(null);
      setStep("uploading");
      trackEvent(SnowplowEvent.ResumeUploadStarted, {
        source: "modal",
        file_type: file.type,
        file_size: file.size,
        variant: "upload_resume_modal",
      });

      const formData = new FormData();
      formData.append("resume", file);
      formData.append("save_profile", "true");

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await axios.post("/users/resume", formData, {
          signal: controller.signal,
        });
        await queryClient.invalidateQueries({ queryKey: ["profile"] });
        posthog?.capture("resume_uploaded", {
          source: "dashboard",
          upload_method: uploadMethod,
        });
        trackEvent(SnowplowEvent.ResumeUploadCompleted, {
          source: "modal",
          variant: "upload_resume_modal",
        });
        setStep("prompt");
        onOpenChange(false);
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }
        showFlash(tResume("resume.error_flash"), "error");
        trackEvent(SnowplowEvent.ResumeUploadFailed, {
          source: "modal",
          error_reason: e instanceof Error ? e.message : "unknown",
          variant: "upload_resume_modal",
        });
        setStep("prompt");
      } finally {
        abortControllerRef.current = null;
      }
    },
    [showFlash, tResume, queryClient, trackEvent],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadFile(file, "file_picker");
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadFile],
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      uploadFile(file, "drag_and_drop");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={cn(
          "max-w-[333px] p-6",
          step === "prompt" ? "gap-2 sm:gap-4 sm:max-w-[600px]" : "!flex flex-col sm:max-w-[450px]",
          step === "uploading" && "h-72 gap-2 sm:gap-6",
        )}
      >
        <DialogHeader className="text-left px-2 pb-2 sm:p-0">
          <DialogTitle>
            <div className="text-[18px] leading-[24px]">
              {step === "prompt" && t("title")}
              {step === "uploading" && t("uploading_title")}
            </div>
          </DialogTitle>
          {step !== "uploading" && (
            <IconButton icon={CloseIcon} size="md" label="Close" onClick={handleClose} />
          )}
        </DialogHeader>

        <DialogDescription>
          <div className={cn(step === "uploading" && "flex-1 flex flex-col")}>
            {step === "prompt" && (
              <Stack gap="12px">
                <img
                  src={isMobile ? quickApplyBannerMobile : quickApplyBannerDesktop}
                  alt={t("title")}
                  className="w-full rounded-lg"
                />
                <div className="py-[12px]">
                  <Body>{t("description")}</Body>
                </div>
                <div
                  className={cn(
                    "flex flex-col items-center justify-center w-full rounded-[5px] gap-1",
                    "p-4 sm:px-[26px] sm:py-[26px]",
                    "cursor-pointer transition-colors border border-dashed",
                    dragOver
                      ? "bg-[var(--green-50)] border-[var(--green-500)]"
                      : "border-[var(--gray-700)]",
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex items-center justify-center gap-3">
                    <UploadIcon size="sm" />
                    <Body>
                      <button
                        type="button"
                        className="underline text-[var(--green-700)]"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        {t("browse")}
                      </button>{" "}
                      {t("drop_hint")}
                    </Body>
                  </div>
                  <Body metadata className="text-center">
                    {t("file_types")}
                  </Body>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.rtf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {uploadError && (
                  <p className="helper-text helper-text--error !ml-0" aria-live="polite">
                    {uploadError}
                  </p>
                )}
              </Stack>
            )}

            {step === "uploading" && (
              <div className="flex items-center justify-center flex-1">
                <LoadingSpinner size="large" color="green" />
              </div>
            )}
          </div>
        </DialogDescription>

        {step === "uploading" && (
          <DialogFooter className="flex-row justify-end mt-auto">
            <SecondaryButton size={buttonSize} onClick={handleClose}>
              {t("cancel")}
            </SecondaryButton>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DashboardUploadResumeModal;
